import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../modules/auth/core/Auth';
import { 
  CostItem, 
  BudgetData, 
  getBudgetData, 
  saveBudgetData, 
  addCostItem, 
  removeCostItem, 
  updateTotalBudget,
  initializeBudget 
} from '../../services/budgetService';

interface BudgetManagerProps {}

export const BudgetManager: React.FC<BudgetManagerProps> = () => {
  const { currentUser } = useAuth();
  const [totalBudget, setTotalBudget] = useState<number>(0);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [newItem, setNewItem] = useState({
    label: '',
    description: '',
    cost: '',
    isLabor: false,
    isMaterials: false,
    isOther: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [budgetSaving, setBudgetSaving] = useState<boolean>(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<{
    label: string;
    description: string;
    cost: string;
    isLabor: boolean;
    isMaterials: boolean;
    isOther: boolean;
  } | null>(null);

  // Calculate totals (only include items where isIncluded is true)
  const includedItems = costItems.filter(item => item.isIncluded !== false); // Default to true if undefined
  const totalCosts = includedItems.reduce((sum, item) => sum + item.cost, 0);
  const laborCosts = includedItems.filter(item => item.isLabor).reduce((sum, item) => sum + item.cost, 0);
  const materialsCosts = includedItems.filter(item => item.isMaterials).reduce((sum, item) => sum + item.cost, 0);
  const otherCosts = includedItems.filter(item => item.isOther).reduce((sum, item) => sum + item.cost, 0);
  const remainingBudget = totalBudget - totalCosts;

  // Calculate Cash In Bank (based on paid items)
  const paidItems = costItems.filter(item => item.isPaid === true);
  const totalPaidCosts = paidItems.reduce((sum, item) => sum + item.cost, 0);
  const cashInBank = totalBudget - totalPaidCosts;

  // Load budget data on component mount
  useEffect(() => {
    const loadBudgetData = async () => {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const budgetData = await getBudgetData(currentUser.id);
        
        if (budgetData) {
          setTotalBudget(budgetData.totalBudget);
          setCostItems(budgetData.costItems || []);
        } else {
          // Initialize empty budget for new user
          await initializeBudget(currentUser.id);
          setTotalBudget(0);
          setCostItems([]);
        }
      } catch (err) {
        console.error('Error loading budget data:', err);
        setError('Failed to load budget data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    loadBudgetData();
  }, [currentUser?.id]);

  // Add new cost item
  const handleAddItem = async () => {
    if (!currentUser?.id) {
      setError('You must be logged in to add cost items');
      return;
    }

    if (!newItem.label.trim()) {
      setError('Item label is required');
      return;
    }
    
    if (!newItem.cost || isNaN(Number(newItem.cost)) || Number(newItem.cost) <= 0) {
      setError('Please enter a valid cost amount');
      return;
    }

    try {
      setSaving(true);
      const newCostItem = await addCostItem(currentUser.id, {
        label: newItem.label.trim(),
        description: newItem.description.trim(),
        cost: Number(newItem.cost),
        isLabor: newItem.isLabor,
        isMaterials: newItem.isMaterials,
        isOther: newItem.isOther
      });

      setCostItems(prev => [...prev, newCostItem]);
      setNewItem({ label: '', description: '', cost: '', isLabor: false, isMaterials: false, isOther: false });
      setError(null);
    } catch (err) {
      console.error('Error adding cost item:', err);
      setError('Failed to add cost item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Remove cost item
  const handleRemoveItem = async (id: string) => {
    if (!currentUser?.id) {
      setError('You must be logged in to remove cost items');
      return;
    }

    try {
      setSaving(true);
      await removeCostItem(currentUser.id, id);
      setCostItems(prev => prev.filter(item => item.id !== id));
      setError(null);
    } catch (err) {
      console.error('Error removing cost item:', err);
      setError('Failed to remove cost item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle budget input change
  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      setTotalBudget(Number(value) || 0);
    }
  };

  // Save budget function
  const handleSaveBudget = async () => {
    if (!currentUser?.id) {
      setError('You must be logged in to save budget');
      return;
    }

    try {
      setBudgetSaving(true);
      await updateTotalBudget(currentUser.id, totalBudget);
      setError(null);
    } catch (err) {
      console.error('Error updating budget:', err);
      setError('Failed to save budget. Please try again.');
    } finally {
      setBudgetSaving(false);
    }
  };

  // Handle new item input changes
  const handleNewItemChange = (field: string, value: string | boolean) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  // Start editing an item
  const handleEditItem = (item: CostItem) => {
    setEditingItemId(item.id);
    setEditingItem({
      label: item.label,
      description: item.description,
      cost: item.cost.toString(),
      isLabor: item.isLabor || false,
      isMaterials: item.isMaterials || false,
      isOther: item.isOther || false
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItem(null);
  };

  // Handle editing item input changes
  const handleEditItemChange = (field: string, value: string | boolean) => {
    if (editingItem) {
      setEditingItem(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  // Save edited item
  const handleSaveEdit = async () => {
    if (!currentUser?.id || !editingItemId || !editingItem) {
      setError('Invalid edit state');
      return;
    }

    if (!editingItem.label.trim()) {
      setError('Item label is required');
      return;
    }
    
    if (!editingItem.cost || isNaN(Number(editingItem.cost)) || Number(editingItem.cost) <= 0) {
      setError('Please enter a valid cost amount');
      return;
    }

    try {
      setSaving(true);
      
      // Update the item in the costItems array
      const updatedItems = costItems.map(item => 
        item.id === editingItemId 
          ? {
              ...item,
              label: editingItem.label.trim(),
              description: editingItem.description.trim(),
              cost: Number(editingItem.cost),
              isLabor: editingItem.isLabor,
              isMaterials: editingItem.isMaterials,
              isOther: editingItem.isOther,
              updatedAt: new Date()
            }
          : item
      );

      // Save to Firebase
      await saveBudgetData(currentUser.id, {
        totalBudget,
        costItems: updatedItems
      });

      // Update local state
      setCostItems(updatedItems);
      setEditingItemId(null);
      setEditingItem(null);
      setError(null);
    } catch (err) {
      console.error('Error updating cost item:', err);
      setError('Failed to update cost item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Move item up
  const handleMoveItemUp = async (index: number) => {
    if (!currentUser?.id || index === 0) return;

    try {
      setSaving(true);
      const updatedItems = [...costItems];
      [updatedItems[index - 1], updatedItems[index]] = [updatedItems[index], updatedItems[index - 1]];

      // Save to Firebase
      await saveBudgetData(currentUser.id, {
        totalBudget,
        costItems: updatedItems
      });

      setCostItems(updatedItems);
      setError(null);
    } catch (err) {
      console.error('Error moving item up:', err);
      setError('Failed to reorder items. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Move item down
  const handleMoveItemDown = async (index: number) => {
    if (!currentUser?.id || index === costItems.length - 1) return;

    try {
      setSaving(true);
      const updatedItems = [...costItems];
      [updatedItems[index], updatedItems[index + 1]] = [updatedItems[index + 1], updatedItems[index]];

      // Save to Firebase
      await saveBudgetData(currentUser.id, {
        totalBudget,
        costItems: updatedItems
      });

      setCostItems(updatedItems);
      setError(null);
    } catch (err) {
      console.error('Error moving item down:', err);
      setError('Failed to reorder items. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle item inclusion in calculations
  const handleToggleInclusion = async (id: string) => {
    if (!currentUser?.id) {
      setError('You must be logged in to toggle item inclusion');
      return;
    }

    try {
      setSaving(true);
      
      // Update the item in the costItems array
      const updatedItems = costItems.map(item => 
        item.id === id 
          ? {
              ...item,
              isIncluded: item.isIncluded !== false ? false : true, // Toggle the inclusion status
              updatedAt: new Date()
            }
          : item
      );

      // Save to Firebase
      await saveBudgetData(currentUser.id, {
        totalBudget,
        costItems: updatedItems
      });

      // Update local state
      setCostItems(updatedItems);
      setError(null);
    } catch (err) {
      console.error('Error toggling item inclusion:', err);
      setError('Failed to toggle item inclusion. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle item paid status
  const handleTogglePaid = async (id: string) => {
    if (!currentUser?.id) {
      setError('You must be logged in to toggle item paid status');
      return;
    }

    try {
      setSaving(true);
      
      // Update the item in the costItems array
      const updatedItems = costItems.map(item => 
        item.id === id 
          ? {
              ...item,
              isPaid: item.isPaid !== true ? true : false, // Toggle the paid status
              updatedAt: new Date()
            }
          : item
      );

      // Save to Firebase
      await saveBudgetData(currentUser.id, {
        totalBudget,
        costItems: updatedItems
      });

      // Update local state
      setCostItems(updatedItems);
      setError(null);
    } catch (err) {
      console.error('Error toggling item paid status:', err);
      setError('Failed to toggle item paid status. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get color classes for item based on toggle states
  const getItemColorClasses = (item: CostItem) => {
    const isIncluded = item.isIncluded !== false; // Default to true if undefined
    const isPaid = item.isPaid === true; // Default to false if undefined
    const isLabor = item.isLabor || false;
    const isMaterials = item.isMaterials || false;
    const isOther = item.isOther || false;
    const toggleCount = [isLabor, isMaterials, isOther].filter(Boolean).length;
    
    // Base classes for excluded items
    if (!isIncluded) {
      return 'bg-light-secondary opacity-50 text-muted'; // Excluded items - faded appearance
    }
    
    // Special styling for paid items
    if (isPaid) {
      return 'bg-light-success border-success text-success'; // Paid items - green with success text
    }
    
    // Classes for included items based on categories
    if (toggleCount > 1) {
      return 'bg-light-info border-info'; // Multiple categories - info blue
    } else if (isLabor) {
      return 'bg-light-primary border-primary'; // Labor only - blue
    } else if (isMaterials) {
      return 'bg-light-warning border-warning'; // Materials only - orange
    } else if (isOther) {
      return 'bg-light-dark border-gray-300'; // Other only - gray
    }
    return ''; // None - default
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Show loading spinner while loading or if user is not authenticated
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-10">
        <div className="text-center">
          <Spinner animation="border" role="status" className="text-primary mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-gray-600">Loading your budget data...</p>
        </div>
      </div>
    );
  }

  // Show authentication required message
  if (!currentUser) {
    return (
      <div className="d-flex justify-content-center align-items-center py-10">
        <div className="text-center">
          <div className="text-gray-600 fs-4 mb-4">
            <i className="bi bi-person-lock fs-1 text-gray-400"></i>
          </div>
          <h3 className="text-gray-800 fs-4 fw-bold mb-2">Authentication Required</h3>
          <p className="text-gray-600 fs-6 mb-0">
            Please log in to access your construction budget.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="budget-manager">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="border-danger mb-4">
          {error}
        </Alert>
      )}
      
      {saving && (
        <Alert variant="info" className="border-info mb-4">
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            Saving changes...
          </div>
        </Alert>
      )}

      {/* Header */}
      <Row className="mb-6">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1 text-gray-900">Construction Cost Calculator</h2>
              <p className="text-gray-600 mb-0">Manage your construction budget and track project costs</p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Budget Input and Cash In Bank */}
      <Row className="mb-6">
        <Col lg={6} className="mb-4 mb-lg-0">
          <Card className="card-flush border-0 shadow-sm h-100">
            <Card.Header className="bg-light-primary border-0">
              <div className="card-title">
                <h3 className="fw-bold text-gray-800 m-0">Total Budget</h3>
              </div>
            </Card.Header>
            <Card.Body className="pt-6">
              <Form.Group>
                <Form.Label className="fs-6 fw-semibold text-gray-700">Enter your total construction budget</Form.Label>
                <div className="input-group input-group-solid">
                  <span className="input-group-text bg-gray-100 border-gray-300">$</span>
                  <Form.Control
                    type="number"
                    placeholder="0.00"
                    value={totalBudget || ''}
                    onChange={handleBudgetChange}
                    className="form-control-solid border-gray-300"
                    min="0"
                    step="0.01"
                  />
                  <Button
                    variant="primary"
                    onClick={handleSaveBudget}
                    disabled={budgetSaving}
                    className="btn-primary"
                  >
                    {budgetSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      'Update Budget'
                    )}
                  </Button>
                </div>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4 mb-lg-0">
          <Card className="card-flush border-0 shadow-sm h-100">
            <Card.Header className="bg-light-primary border-0">
              <div className="card-title">
                <h3 className="fw-bold text-gray-800 m-0">Cash In Bank</h3>
              </div>
            </Card.Header>
            <Card.Body className="pt-6 d-flex flex-column justify-content-center text-center">
              <div className={`display-4 fw-bold ${cashInBank >= 0 ? 'text-success' : 'text-danger'} mb-2`}>
                {formatCurrency(cashInBank)}
              </div>
              <div className="fs-6 fw-semibold text-gray-600">
                Actual money remaining based on paid items
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="text-center bg-light-warning border-warning">
            <Card.Body className="py-4">
              <div className="fs-3 fw-bold text-warning">{formatCurrency(totalCosts)}</div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">Total Costs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className={`text-center ${remainingBudget >= 0 ? 'bg-light-primary border-primary' : 'bg-light-danger border-danger'}`}>
            <Card.Body className="py-4">
              <div className={`fs-3 fw-bold ${remainingBudget >= 0 ? 'text-primary' : 'text-danger'}`}>
                {formatCurrency(remainingBudget)}
              </div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">
                {remainingBudget >= 0 ? 'Remaining Budget' : 'Over Budget'}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="text-center bg-light-secondary border-secondary">
            <Card.Body className="py-4">
              <div className="fs-3 fw-bold text-secondary">{formatCurrency(totalPaidCosts)}</div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">Total Paid</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Breakdown Cards */}
      <Row className="mb-6">
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="text-center bg-light-info border-info">
            <Card.Body className="py-4">
              <div className="fs-4 fw-bold text-info">{formatCurrency(laborCosts)}</div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">Labor Costs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="text-center bg-light-secondary border-secondary">
            <Card.Body className="py-4">
              <div className="fs-4 fw-bold text-secondary">{formatCurrency(materialsCosts)}</div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">Materials Costs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="text-center bg-light-dark border-gray-300">
            <Card.Body className="py-4">
              <div className="fs-4 fw-bold text-gray-700">{formatCurrency(otherCosts)}</div>
              <div className="fw-semibold text-gray-600 mt-1 fs-7">Other Costs</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add New Cost Item Form */}
      <Row className="mb-6">
        <Col>
          <Card className="card-flush border-0 shadow-sm">
            <Card.Header className="bg-light-info border-0">
              <div className="card-title">
                <h3 className="fw-bold text-gray-800 m-0">Add Cost Item</h3>
              </div>
            </Card.Header>
            <Card.Body className="pt-6">
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fs-6 fw-semibold text-gray-700 required">Item Label</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Foundation, Roofing, Plumbing"
                      value={newItem.label}
                      onChange={(e) => handleNewItemChange('label', e.target.value)}
                      className="form-control-solid border-gray-300"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fs-6 fw-semibold text-gray-700">Description</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Additional details (optional)"
                      value={newItem.description}
                      onChange={(e) => handleNewItemChange('description', e.target.value)}
                      className="form-control-solid border-gray-300"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fs-6 fw-semibold text-gray-700 required">Cost</Form.Label>
                    <div className="input-group input-group-solid">
                      <span className="input-group-text bg-gray-100 border-gray-300">$</span>
                      <Form.Control
                        type="number"
                        placeholder="0.00"
                        value={newItem.cost}
                        onChange={(e) => handleNewItemChange('cost', e.target.value)}
                        className="form-control-solid border-gray-300"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fs-6 fw-semibold text-gray-700">Type</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      <Form.Check
                        type="switch"
                        id="new-labor-switch"
                        label="Labor"
                        checked={newItem.isLabor}
                        onChange={(e) => handleNewItemChange('isLabor', e.target.checked)}
                        className="form-check-primary"
                      />
                      <Form.Check
                        type="switch"
                        id="new-materials-switch"
                        label="Materials"
                        checked={newItem.isMaterials}
                        onChange={(e) => handleNewItemChange('isMaterials', e.target.checked)}
                        className="form-check-warning"
                      />
                      <Form.Check
                        type="switch"
                        id="new-other-switch"
                        label="Other"
                        checked={newItem.isOther}
                        onChange={(e) => handleNewItemChange('isOther', e.target.checked)}
                        className="form-check-dark"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    onClick={handleAddItem}
                    className="btn-primary mb-4"
                    disabled={!newItem.label.trim() || !newItem.cost || saving || editingItemId !== null}
                    title="Add cost item"
                  >
                    {saving ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="bi bi-plus-circle"></i>
                    )}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cost Items Table */}
      <Row>
        <Col>
          <Card className="card-flush border-0 shadow-sm">
            <Card.Header className="bg-light-secondary border-0">
              <div className="card-title">
                <h3 className="fw-bold text-gray-800 m-0">Cost Items ({costItems.length})</h3>
              </div>
              <div className="card-toolbar">
                <div className="text-muted fs-7">
                  <i className="bi bi-info-circle me-1"></i>
                  Use "Include" to control budget planning and "Paid" to track actual expenses
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {costItems.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-gray-600 fs-4 mb-4">
                    <i className="bi bi-calculator fs-1 text-gray-400"></i>
                  </div>
                  <h3 className="text-gray-800 fs-4 fw-bold mb-2">No cost items yet</h3>
                  <p className="text-gray-600 fs-6 mb-0">
                    Add your first cost item using the form above to start tracking your construction expenses.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="table-row-dashed gy-5">
                    <thead>
                      <tr className="fw-semibold fs-6 text-gray-800 bg-light">
                        <th className="ps-6">Item</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th className="text-center">Include</th>
                        <th className="text-center">Paid</th>
                        <th className="text-end">Cost</th>
                        <th className="text-end pe-6">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costItems.map((item, index) => (
                        <tr key={item.id} className={getItemColorClasses(item)}>
                          {editingItemId === item.id ? (
                            // Edit mode
                            <>
                              <td className="ps-6">
                                <Form.Control
                                  type="text"
                                  value={editingItem?.label || ''}
                                  onChange={(e) => handleEditItemChange('label', e.target.value)}
                                  className="form-control-sm"
                                  placeholder="Item label"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={editingItem?.description || ''}
                                  onChange={(e) => handleEditItemChange('description', e.target.value)}
                                  className="form-control-sm"
                                  placeholder="Description"
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="form-check form-switch form-check-sm">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit-labor-${item.id}`}
                                      checked={editingItem?.isLabor || false}
                                      onChange={(e) => handleEditItemChange('isLabor', e.target.checked)}
                                      title="Labor"
                                    />
                                    <label className="form-check-label text-primary fw-bold" htmlFor={`edit-labor-${item.id}`}>
                                      L
                                    </label>
                                  </div>
                                  <div className="form-check form-switch form-check-sm">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit-materials-${item.id}`}
                                      checked={editingItem?.isMaterials || false}
                                      onChange={(e) => handleEditItemChange('isMaterials', e.target.checked)}
                                      title="Materials"
                                    />
                                    <label className="form-check-label text-warning fw-bold" htmlFor={`edit-materials-${item.id}`}>
                                      M
                                    </label>
                                  </div>
                                  <div className="form-check form-switch form-check-sm">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit-other-${item.id}`}
                                      checked={editingItem?.isOther || false}
                                      onChange={(e) => handleEditItemChange('isOther', e.target.checked)}
                                      title="Other"
                                    />
                                    <label className="form-check-label text-gray-700 fw-bold" htmlFor={`edit-other-${item.id}`}>
                                      O
                                    </label>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  id={`edit-include-${item.id}`}
                                  checked={item.isIncluded !== false} // Default to true if undefined
                                  onChange={(e) => handleToggleInclusion(item.id)}
                                  disabled={saving}
                                  title="Include in total calculations"
                                  className="d-flex justify-content-center"
                                />
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  id={`edit-paid-${item.id}`}
                                  checked={item.isPaid === true} // Default to false if undefined
                                  onChange={(e) => handleTogglePaid(item.id)}
                                  disabled={saving}
                                  title="Mark as paid"
                                  className="d-flex justify-content-center"
                                />
                              </td>
                              <td className="text-end">
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text">$</span>
                                  <Form.Control
                                    type="number"
                                    value={editingItem?.cost || ''}
                                    onChange={(e) => handleEditItemChange('cost', e.target.value)}
                                    className="form-control-sm text-end"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>
                              </td>
                              <td className="text-end pe-6">
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                    title="Save changes"
                                  >
                                    {saving ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <i className="bi bi-check"></i>
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleCancelEdit}
                                    disabled={saving}
                                    title="Cancel edit"
                                  >
                                    <i className="bi bi-x"></i>
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            // View mode
                            <>
                              <td className="ps-6">
                                <div className="fw-bold text-gray-800">{item.label}</div>
                              </td>
                              <td>
                                <div className="text-gray-600">{item.description || '—'}</div>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  {item.isLabor && (
                                    <span className="badge badge-light-primary badge-sm">Labor</span>
                                  )}
                                  {item.isMaterials && (
                                    <span className="badge badge-light-warning badge-sm">Materials</span>
                                  )}
                                  {item.isOther && (
                                    <span className="badge badge-light-dark badge-sm">Other</span>
                                  )}
                                  {!item.isLabor && !item.isMaterials && !item.isOther && (
                                    <span className="text-muted small">—</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  id={`include-${item.id}`}
                                  checked={item.isIncluded !== false} // Default to true if undefined
                                  onChange={(e) => handleToggleInclusion(item.id)}
                                  disabled={saving || editingItemId !== null}
                                  title="Include in total calculations"
                                  className="d-flex justify-content-center"
                                />
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  id={`paid-${item.id}`}
                                  checked={item.isPaid === true} // Default to false if undefined
                                  onChange={(e) => handleTogglePaid(item.id)}
                                  disabled={saving || editingItemId !== null}
                                  title="Mark as paid"
                                  className="d-flex justify-content-center"
                                />
                              </td>
                              <td className="text-end">
                                <div className="fw-bold text-gray-800">{formatCurrency(item.cost)}</div>
                              </td>
                              <td className="text-end pe-6">
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleMoveItemUp(index)}
                                    disabled={saving || editingItemId !== null || index === 0}
                                    title="Move up"
                                  >
                                    <i className="bi bi-arrow-up"></i>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleMoveItemDown(index)}
                                    disabled={saving || editingItemId !== null || index === costItems.length - 1}
                                    title="Move down"
                                  >
                                    <i className="bi bi-arrow-down"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditItem(item)}
                                    disabled={saving || editingItemId !== null}
                                    title="Edit item"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleRemoveItem(item.id)}
                                    className="btn-outline-danger btn-active-light-danger"
                                    disabled={saving || editingItemId !== null}
                                    title="Delete item"
                                  >
                                    {saving ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <i className="bi bi-trash"></i>
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                      {/* Total Row */}
                      <tr className="border-top border-2 border-gray-300">
                        <td className="ps-6">
                          <div className="fw-bold text-gray-800 fs-5">Total</div>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-end">
                          <div className="fw-bold text-gray-800 fs-5">{formatCurrency(totalCosts)}</div>
                        </td>
                        <td className="pe-6"></td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Export with the same name for compatibility
export const QuoteManager = BudgetManager;